import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import styled from '@emotion/styled';
import { Icon } from '@iconify/react';
import StepperVertical from '../../components/Stepper/StepperVertical';
import { useAppSelector } from '../../hooks/useRedux';
import { lifeCycleSnapshot } from '../../services/vehicles';
import useMetaData from '../../hooks/useMetaData';
import { META_DATA_MODULES, META_DATA_SUB_MODULES } from '../../utils/constants';
import GridLabels from '../VehicleTabs/GridLabels';
import { numToWords } from '../../utils/string';

const AccordionDrop = styled('div')(({ theme }) => ({
  '.MuiAccordionSummary-gutters': { minHeight: 'auto !important' },
  '.MuiAccordionSummary-contentGutters, .Mui-expanded': { margin: '0 !important' },
  '.MuiCollapse-wrapperInner': { marginTop: '20px' },
}));

// handling content type
export const contentType = (type: string, value: any, label: string, textSize = 14) => {
  switch (type) {
    case 'Date':
      return (
        <Typography fontSize={textSize} variant="caption" component="div">
          {moment(value, 'Y-MM-DD hh:mm:ss').format('MMM DD, Y')}
          {label && label !== '' ? ` - ${label}` : null}
        </Typography>
      );
    case 'Address':
      return (
        <Typography fontSize={textSize} color="#717171" variant="caption" component="div">
          {label && label !== '' ? `${label}: ` : null}
          {value}
        </Typography>
      );
    default:
      return (
        <Typography fontSize={textSize} variant="caption" component="div">
          {label} {value && value !== '' ? ` - ${value}` : null}
        </Typography>
      );
  }
};

// handling content render
export const handleContent = (obj: any, meta: any, showChild?: string) => {
  // handling history dates
  const handlingDateLabel = (date: string, index: number) => {
    if (index === 0)
      return (
        <Typography sx={{ fontSize: '12px', color: '#40423F', fontStyle: 'italic' }}>
          {`${moment(date, 'Y-MM-DD hh:mm:ss')?.format('MMM DD, Y')} - Original Planned Broadcast Date`}
        </Typography>
      );
    if (index > 0)
      return (
        <Typography sx={{ fontSize: '12px', color: '#40423F', fontStyle: 'italic' }}>
          {`${moment(date, 'Y-MM-DD hh:mm:ss')?.format('MMM DD, Y')} - Planned Broadcast ${numToWords(
            index
          )} Assigned Date`}
        </Typography>
      );
    return '';
  };

  const content = meta?.rows?.map((item: any) => {
    console.log(
      typeof obj?.[item?.propertyKey] === 'object' ? obj?.[item?.propertyKey]?.filter((fill: any) => fill) : null,
      'filter'
    );
    return (
      <>
        {obj?.[item?.propertyKey] && obj?.[item?.propertyKey] !== '' && typeof obj?.[item?.propertyKey] !== 'object'
          ? contentType(
              item?.propertyType,
              obj?.[item?.propertyKey],
              item?.propertyLabel,
              item?.propertyType === 'Address' ? 12 : 14
            )
          : null}

        {item?.children?.length &&
          showChild !== 'false' &&
          item?.children?.map((subItem: any) => {
            return (
              <Box
                sx={{ borderLeft: '2px solid #dbd4c5', paddingLeft: '15px' }}
                marginTop={subItem?.caption && subItem?.caption !== '' ? 2 : 0}
                marginBottom={subItem?.caption && subItem?.caption !== '' ? 2 : 0}
              >
                {subItem?.caption && subItem?.caption !== '' ? (
                  <Typography variant="h5" fontSize={12} fontWeight={500} paddingBottom="3px">
                    {subItem?.caption}
                  </Typography>
                ) : null}
                {subItem?.rows?.map((chd: any) => {
                  if (obj?.[chd?.propertyKey] && obj?.[chd?.propertyKey] !== '')
                    return (
                      <>
                        {contentType(
                          chd?.propertyType,
                          obj?.[chd?.propertyKey],
                          chd?.propertyLabel,
                          chd?.propertyType === 'Address' ? 10 : 12
                        )}
                      </>
                    );
                  return null;
                })}
              </Box>
            );
          })}
        {typeof obj?.[item?.propertyKey] === 'object' ? (
          <AccordionDrop>
            <Accordion
              sx={{
                boxShadow: 'none',
                borderRadius: '0px',
                border: 'none',
                padding: '0px',
                margin: '0px',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon icon="la:angle-down" width="12" color="#456BD9" />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                sx={{
                  '&:before': { display: 'none' },
                  padding: '0px',
                  flexGrow: '0',
                  justifyContent: 'start',
                  display: 'inline-flex',
                  color: '#456BD9',
                  margin: '0',
                }}
              >
                <Typography sx={{ fontSize: '12px', paddingRight: '5px', margin: '0px' }}>
                  {item?.propertyLabel}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '0px 0 10px 0', margin: '-20px 0 0 0' }}>
                {obj?.[item?.propertyKey]?.filter((fill: any) => fill)?.length > 1 ? (
                  obj?.[item?.propertyKey]
                    ?.filter((fill: any) => fill)
                    ?.slice?.(0, obj[item.propertyKey].filter((fill: any) => fill).length - 1)
                    ?.map((date: any, i: number) => {
                      return handlingDateLabel(date, i);
                    })
                ) : (
                  <Typography fontSize={12} color="#717171" variant="caption" component="div">
                    No history found
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </AccordionDrop>
        ) : null}
      </>
    );
  });

  return content;
};

const Reservation = () => {
  // use hooks
  const { reservationId } = useParams<{ reservationId: string }>();
  const metaData = useMetaData(META_DATA_MODULES.v360Page, META_DATA_SUB_MODULES.LifeCycleSnapShot);

  // global states
  const vehicleData = useAppSelector((state) => state.vehicle?.data);
  const { currentStage } = useAppSelector((state) => state.vehicle);

  // local sates
  const [reservedData, setReservedData] = useState<any>(null);

  // constants
  const stepsVertical = reservedData?.stageInformation
    ?.sort((a: any, b: any) => parseFloat(b?.sequence) - parseFloat(a?.sequence))
    ?.filter(
      (fil: { ruleResult: { isCompleted: boolean }; subStage: string; showSubStage: boolean }) => fil?.showSubStage
    )
    ?.map((item: any) => {
      return {
        id: '1',
        label: (
          <Typography variant="body2" fontWeight={500} fontSize={16} color="#1F1F1F">
            {metaData?.fields?.[item?.subStage]?.caption}
          </Typography>
        ),
        content: <>{handleContent(item?.ruleResult, metaData?.fields?.[item?.subStage])}</>,
        isCompleted: item?.ruleResult?.isCompleted,
      };
    });

  // fetching record
  const fetchReservedData = useCallback(async () => {
    try {
      const resp = await lifeCycleSnapshot({
        method: 'POST',
        data: {
          reservationId,
          stages: ['Reserved'],
          langCode: 'en-us',
        },
      });
      setReservedData(resp?.data?.data?.[0]);
    } catch (error) {
      console.log(error);
    }
  }, [reservationId]);

  // lifecycle hooks
  useEffect(() => {
    fetchReservedData();
  }, [fetchReservedData]);

  if (!reservedData)
    return (
      <Box sx={{ position: 'relative', minHeight: '250px' }}>
        <CircularProgress
          color="inherit"
          size={18}
          sx={{ position: 'absolute', left: '0', right: '0', top: '50%', margin: '0 auto' }}
        />
      </Box>
    );
  return (
    <Box>
      <Typography fontSize={20} mb={1.2} fontWeight={500} lineHeight="24px">
        Reservation
      </Typography>
      <Grid item xs={12} mb={1}>
        <Divider sx={{ borderColor: '#e3e3e3' }} />
      </Grid>
      {/* - - canceled order - - - - */}
      {currentStage === 'Cancelled' ? (
        <Box
          sx={{
            border: '1px solid #E04732',
            borderRadius: '4px',
            padding: '5px 8px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: 'calc(100% - 50px)',
          }}
          my={1.5}
        >
          <Icon icon="ri:information-line" fontSize={14} color="#E04732" />
          <Typography fontSize={12} fontWeight={400} color="#E04732" marginLeft={1.2} flexGrow={1}>
            Order Canceled
          </Typography>
        </Box>
      ) : null}

      <StepperVertical steps={stepsVertical} activeStep={stepsVertical?.length ?? 10} />
      <Box sx={{ width: '100%', my: 4 }}>
        <Typography fontSize={14} mb={1} fontWeight={500}>
          Reservation Details
        </Typography>
        <Divider sx={{ borderColor: '#e3e3e3', borderStyle: 'dashed' }} />
        <Grid container spacing={2} mt={0}>
          <GridLabels
            labels={[
              {
                label: 'Reservation Number',
                title: vehicleData?.reservationId,
                type: 'Link',
                link: vehicleData?.sForceURL ?? '#',
              },
              { label: 'Reservation Placed On', title: moment(vehicleData?.reservationDate).format('MMM DD, Y') },
            ]}
            labelSx={{ fontSize: '14px', fontWeight: '400', color: '#717171' }}
            titleSx={{ fontSize: '16px', fontWeight: '500', color: '#3E3E3E' }}
          />
        </Grid>
      </Box>
    </Box>
  );
};
export default Reservation;
